

export const dynamic = 'force-dynamic';

import { useState } from "react";
import { Link } from 'react-router-dom';
import DashboardLayout from "../components/DashboardLayout";
import { 
  FileSpreadsheet, 
  TrendingUp, 
  Users, 
  Receipt, 
  CreditCard,
  Calendar,
  Package,
  Building2,
  ArrowRight,
  BarChart3,
  Wallet,
  Clock,
  Lock
} from "lucide-react";

interface ReportCard {
  title: string;
  description: string;
  href: string;
  icon: any;
  color: string;
  bgColor: string;
  borderColor: string;
  comingSoon?: boolean;
}

export default function ReportsHubPage() {
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);

  // Available reports (pages that exist)
  const availableReports: ReportCard[] = [
    {
      title: "Financial Summary",
      description: "Revenue, expenses, profit & loss overview",
      href: "/reports/financial-summary",
      icon: FileSpreadsheet,
      color: "text-emerald-600",
      bgColor: "bg-emerald-50",
      borderColor: "border-emerald-200 hover:border-emerald-400",
    },
    {
      title: "Revenue Trends",
      description: "Monthly revenue analysis and comparisons",
      href: "/reports/revenue",
      icon: TrendingUp,
      color: "text-orange-600",
      bgColor: "bg-orange-50",
      borderColor: "border-orange-200 hover:border-orange-400",
    },
    {
      title: "Client Analytics",
      description: "Per-client revenue, payments, and dues",
      href: "/reports/clients",
      icon: Users,
      color: "text-blue-600",
      bgColor: "bg-blue-50",
      borderColor: "border-blue-200 hover:border-blue-400",
    },
    {
      title: "Enquiry Funnel",
      description: "Conversion rates from enquiry to event",
      href: "/reports/enquiries",
      icon: BarChart3,
      color: "text-indigo-600",
      bgColor: "bg-indigo-50",
      borderColor: "border-indigo-200 hover:border-indigo-400",
    },
    {
      title: "Package Analytics",
      description: "Package popularity and revenue insights",
      href: "/reports/packages",
      icon: Package,
      color: "text-violet-600",
      bgColor: "bg-violet-50",
      borderColor: "border-violet-200 hover:border-violet-400",
    },
    {
      title: "Event Analytics",
      description: "Event performance, bookings, and trends",
      href: "/reports/events",
      icon: Calendar,
      color: "text-sky-600",
      bgColor: "bg-sky-50",
      borderColor: "border-sky-200 hover:border-sky-400",
    },
    {
      title: "Vendor Performance",
      description: "Vendor payments, ratings, and assignments",
      href: "/reports/vendors/performance",
      icon: Building2,
      color: "text-teal-600",
      bgColor: "bg-teal-50",
      borderColor: "border-teal-200 hover:border-teal-400",
    },
    {
      title: "Expenses Report",
      description: "Detailed expense tracking and analysis",
      href: "/reports/expenses",
      icon: Receipt,
      color: "text-amber-600",
      bgColor: "bg-amber-50",
      borderColor: "border-amber-200 hover:border-amber-400",
    },
    {
      title: "Client Payments",
      description: "Track all client payments and pending dues",
      href: "/client-payments",
      icon: CreditCard,
      color: "text-rose-600",
      bgColor: "bg-rose-50",
      borderColor: "border-rose-200 hover:border-rose-400",
    },
    {
      title: "Venue Utilization",
      description: "Date-wise venue & room occupancy",
      href: "/reports/venues",
      icon: Building2,
      color: "text-teal-600",
      bgColor: "bg-teal-50",
      borderColor: "border-teal-200 hover:border-teal-400",
    },
    {
      title: "Vendor Payments",
      description: "Track vendor payments and commissions",
      href: "/vendor-payments",
      icon: Wallet,
      color: "text-cyan-600",
      bgColor: "bg-cyan-50",
      borderColor: "border-cyan-200 hover:border-cyan-400",
    },
  ];

  // Coming soon reports
  const comingSoonReports: ReportCard[] = [];

  const ReportCard = ({ report }: { report: ReportCard }) => {
    const Icon = report.icon;
    const isHovered = hoveredCard === report.title;
    
    if (report.comingSoon) {
      return (
        <div
          className={`relative ${report.bgColor} rounded-lg p-4 border ${report.borderColor} opacity-60 cursor-not-allowed`}
        >
          <div className="absolute top-2 right-2">
            <span className="text-[10px] font-medium px-2 py-0.5 bg-stone-200 text-stone-600 rounded-full flex items-center gap-1">
              <Clock className="w-3 h-3" />
              Soon
            </span>
          </div>
          <div className={`w-8 h-8 rounded-lg bg-white/80 flex items-center justify-center mb-2 ${report.color}`}>
            <Icon className="w-4 h-4" />
          </div>
          <h3 className="text-sm font-semibold text-stone-700 mb-0.5">{report.title}</h3>
          <p className="text-xs text-stone-500 line-clamp-2">{report.description}</p>
        </div>
      );
    }

    return (
      <Link
        to={report.href}
        onMouseEnter={() => setHoveredCard(report.title)}
        onMouseLeave={() => setHoveredCard(null)}
        className={`relative ${report.bgColor} rounded-lg p-4 border ${report.borderColor} transition-all duration-200 hover:shadow-md ${
          isHovered ? 'shadow-sm -translate-y-0.5' : ''
        }`}
      >
        <div className={`w-8 h-8 rounded-lg bg-white/80 flex items-center justify-center mb-2 ${report.color}`}>
          <Icon className="w-4 h-4" />
        </div>
        <h3 className="text-sm font-semibold text-stone-800 mb-0.5">{report.title}</h3>
        <p className="text-xs text-stone-600 line-clamp-2 mb-2">{report.description}</p>
        <div className={`flex items-center gap-1 text-xs font-medium ${report.color} transition-transform ${
          isHovered ? 'translate-x-0.5' : ''
        }`}>
          Open
          <ArrowRight className="w-3 h-3" />
        </div>
      </Link>
    );
  };

  return (
    <DashboardLayout>
      <div className="space-y-4">
        {/* Compact Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-amber-600" />
            <h1 className="text-lg font-bold text-stone-800">Reports</h1>
          </div>
          <span className="text-xs text-stone-500">
            {availableReports.length} reports
          </span>
        </div>

        {/* Available Reports - Compact Grid */}
        <div>
          <h2 className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-2">
            Available
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-5 gap-2">
            {availableReports.map((report) => (
              <ReportCard key={report.title} report={report} />
            ))}
          </div>
        </div>

        {/* Quick Links Bar */}
        <div className="bg-stone-50 rounded-lg p-3 border border-stone-200">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-stone-500 mr-1">Quick:</span>
            <Link 
              to="/expenses"
              className="text-xs px-2 py-1 bg-white rounded border border-stone-200 text-stone-600 hover:text-amber-600 hover:border-amber-300 transition-colors"
            >
              Manage Expenses
            </Link>
            <Link 
              to="/reports/expenses"
              className="text-xs px-2 py-1 bg-white rounded border border-stone-200 text-stone-600 hover:text-orange-600 hover:border-orange-300 transition-colors"
            >
              Expenses Report
            </Link>
            <Link 
              to="/client-payments"
              className="text-xs px-2 py-1 bg-white rounded border border-stone-200 text-stone-600 hover:text-rose-600 hover:border-rose-300 transition-colors"
            >
              Client Payments
            </Link>
            <Link 
              to="/vendor-payments"
              className="text-xs px-2 py-1 bg-white rounded border border-stone-200 text-stone-600 hover:text-cyan-600 hover:border-cyan-300 transition-colors"
            >
              Vendor Payments
            </Link>
            <Link 
              to="/reports/events"
              className="text-xs px-2 py-1 bg-white rounded border border-stone-200 text-stone-600 hover:text-blue-600 hover:border-blue-300 transition-colors"
            >
              Events
            </Link>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
